import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { encryptData } from '../../utils/cryptoUtils';
import '../common.scss';

// ----------------------------------------------------------------------
const emptyFleetRow = () => ({
  vehicle_no: '',
  vehicle_type: '',
  vehicle_maker: '',
  financer_name: '',
  loan_status: 'No Loan',
  owner_name: '',
  status: 'Active',
});
// ----------------------------------------------------------------------

const FleetDetails = (props) => {
  const { leadId } = props;

  const [fleetRows, setFleetRows] = useState([emptyFleetRow()]);
  const [preloaderStatus, setPreloaderStatus] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // ---- Fetch existing fleet rows for this lead ----
  const getFleetDetails = async () => {
    if (!leadId) return;
    try {
      setLoadingList(true);

      const obj = { lead_id: leadId };

      const response = await fetch(`${config.API_BASE_URL}getFleetDetails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(obj),
      });

      const result = await response.json();

      if (result?.success === 1) {
        const data = result?.data || [];
        if (Array.isArray(data) && data.length > 0) {
          setFleetRows(data);
        } else {
          setFleetRows([emptyFleetRow()]);
        }
      } else {
        setFleetRows([emptyFleetRow()]);
      }
    } catch (error) {
      console.log('Fleet Details fetch error:', error);
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    getFleetDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  // ---- Change handler for a specific row ----
  const handleFleetChange = (index, e) => {
    const { name, value } = e.target;

    setFleetRows((prevRows) => {
      const updated = [...prevRows];
      updated[index] = { ...updated[index], [name]: value };

      if (name === 'financer_name') {
        const v = (value || '').trim().toLowerCase();
        updated[index].loan_status = v === '' || v === 'no loan' || v === 'no financer' ? 'No Loan' : 'Loan';
      }

      return updated;
    });
  };

  // ---- Add More: puri form block copy karke naya row jod do ----
  const handleAddMore = () => {
    setFleetRows((prevRows) => [...prevRows, emptyFleetRow()]);
  };

  // ---- Ek row hata do ----
  const handleRemoveRow = (index) => {
    setFleetRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  // ---- Save: sabhi rows ek saath backend ko bhejo ----
  const handleSave = async () => {
    const hasEmptyVehicleNo = fleetRows.some((row) => (row.vehicle_no || '').trim() === '');
    if (hasEmptyVehicleNo) {
      toast.error('Vehicle No is required for all rows.', { position: 'top-right' });
      return;
    }

    try {
      setPreloaderStatus(true);

      const params = { lead_id: leadId, fleetData: fleetRows };

      const response = await fetch(`${config.API_BASE_URL}manageFleetDetails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encryptData(params),
      });

      const result = await response.json();

      if (result?.success === 1) {
        toast.success(result.message, { position: 'top-right' });
        getFleetDetails();
      } else {
        toast.error(result?.message || 'Something went wrong.', { position: 'top-right' });
      }
    } catch (error) {
      console.error('Error in Fleet Details save:', error);
      toast.error('An error occurred. Please try again.', { position: 'top-right' });
    } finally {
      setPreloaderStatus(false);
    }
  };

  return (
    <div className="fleet_details_section mt-3">
      {loadingList ? (
        <p>Loading fleet details...</p>
      ) : (
        <div>
          {fleetRows.map((row, index) => (
            <div key={`fleet-row-${index}`} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '18px' }}>
              {/* Index circle - OUTSIDE the box, to the left */}
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  minWidth: '30px',
                  borderRadius: '50%',
                  background: '#0d6efd',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '14px',
                  marginRight: '14px',
                  marginTop: '18px',
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </div>

              {/* Actual bordered box */}
              <div className="row border rounded p-3 mx-0" style={{ position: 'relative', flex: 1 }}>
                {fleetRows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveRow(index)}
                    title="Remove this vehicle"
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '26px',
                      height: '26px',
                      borderRadius: '50%',
                      border: 'none',
                      background: '#dc3545',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      fontSize: '14px',
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                )}

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`vehicle_no_${index}`}>
                      <span>
                        Vehicle No <a style={{ color: 'red' }}>*</a>
                      </span>
                      <input
                        type="text"
                        id={`vehicle_no_${index}`}
                        name="vehicle_no"
                        className="form-control"
                        value={row.vehicle_no}
                        onChange={(e) => handleFleetChange(index, e)}
                      />
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`vehicle_type_${index}`}>
                      <span>Vehicle Type</span>
                      <input
                        type="text"
                        id={`vehicle_type_${index}`}
                        name="vehicle_type"
                        className="form-control"
                        placeholder="e.g. 20 FEET / EV VEH CLE"
                        value={row.vehicle_type}
                        onChange={(e) => handleFleetChange(index, e)}
                      />
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`vehicle_maker_${index}`}>
                      <span>Vehicle Maker</span>
                      <input
                        type="text"
                        id={`vehicle_maker_${index}`}
                        name="vehicle_maker"
                        className="form-control"
                        placeholder="e.g. Eicher / TATA Motor / Mahindra"
                        value={row.vehicle_maker}
                        onChange={(e) => handleFleetChange(index, e)}
                      />
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`financer_name_${index}`}>
                      <span>Financer</span>
                      <input
                        type="text"
                        id={`financer_name_${index}`}
                        name="financer_name"
                        className="form-control"
                        placeholder="No Financer if not financed"
                        value={row.financer_name}
                        onChange={(e) => handleFleetChange(index, e)}
                      />
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`loan_status_${index}`}>
                      <span>Loan Status</span>
                      <select
                        id={`loan_status_${index}`}
                        name="loan_status"
                        className="form-select"
                        value={row.loan_status}
                        onChange={(e) => handleFleetChange(index, e)}
                      >
                        <option value="No Loan">No Loan</option>
                        <option value="Loan">Loan</option>
                      </select>
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`owner_name_${index}`}>
                      <span>Owner Name of Vehicle</span>
                      <input
                        type="text"
                        id={`owner_name_${index}`}
                        name="owner_name"
                        className="form-control"
                        value={row.owner_name}
                        onChange={(e) => handleFleetChange(index, e)}
                      />
                    </label>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor={`status_${index}`}>
                      <span>Status</span>
                      <select
                        id={`status_${index}`}
                        name="status"
                        className="form-select"
                        value={row.status}
                        onChange={(e) => handleFleetChange(index, e)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="row mx-0 mt-2 mb-4">
            <div className="col-md-12">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleAddMore}>
                <i className="bi bi-plus-circle"> </i> Add More
              </button>
            </div>
          </div>

          <div className="row mx-0">
            <div className="col-md-12">
              <div className="form-group mb-0">
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={preloaderStatus}>
                  {preloaderStatus ? 'Saving...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetDetails;
